import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@wlb/shared/context/AuthContext';
import { useAdminSession } from '@wlb/shared/context/AdminSessionContext';
import { useTheme } from '@wlb/shared/theme';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { MEMBER_SITE_URL } from '../../config/appLinks';
import { clearAsyncResourceCache } from '../../hooks/useAsyncResource';
import { BrandLogo } from '../ui/BrandLogo';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { IconButton } from '../ui/IconButton';
import { findNavItem, findNavParent, NAV_SECTIONS, type AdminNavItem } from './navItems';
import './admin-shell.css';

/* Colon-separated, not dotted: the i18n coverage test treats dotted lowercase
   string literals as translation keys, and a storage key is not one. */
const COLLAPSE_STORAGE_KEY = 'wlb:admin:nav-collapsed';

/**
 * The rail's width is a workspace preference, not per-visit state: an operator
 * who works in the icon rail should not have to re-collapse it after every
 * reload. A failed read (private mode, storage disabled) just means the default.
 */
const readCollapsed = (): boolean => {
  try {
    return window.localStorage.getItem(COLLAPSE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const isWithin = (item: AdminNavItem, pathname: string): boolean =>
  pathname === item.to || pathname.startsWith(`${item.to}/`);

export function AdminShell() {
  const { t } = useTranslation('admin');
  const { logout } = useAuth();
  const { adminUser } = useAdminSession();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [railCollapsed, setRailCollapsed] = useState(readCollapsed);
  /**
   * Only holds groups the operator opened or closed by hand. Everything else
   * falls back to "open when the current route is inside it", so a deep link
   * lands with its sub-module list already visible.
   */
  const [groupOverrides, setGroupOverrides] = useState<Record<string, boolean>>({});
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  /**
   * The content region scrolls instead of the document, and it survives a route
   * change, so a new page would otherwise open at the offset the previous one
   * was left at. Keyed on the path only: filters live in the query string, and
   * resetting on those would yank the list away mid-typing.
   */
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [location.pathname]);

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSE_STORAGE_KEY, String(railCollapsed));
    } catch {
      // A preference that cannot be stored is not worth failing a render over.
    }
  }, [railCollapsed]);

  // The overlay rail covers the content on small screens, so Escape has to get
  // back out of it.
  useEffect(() => {
    if (!mobileNavOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileNavOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileNavOpen]);

  const signOut = useCallback(async () => {
    navigate('/login', { replace: true });
    // Member records must not survive into the next operator's session.
    clearAsyncResourceCache();
    await logout();
  }, [logout, navigate]);

  const activeItem = findNavItem(location.pathname);
  const parentItem = findNavParent(location.pathname);
  const displayName =
    adminUser?.firstName || adminUser?.userName || adminUser?.email || '';

  const isGroupOpen = (item: AdminNavItem): boolean =>
    groupOverrides[item.to] ?? isWithin(item, location.pathname);

  const toggleGroup = (item: AdminNavItem) => {
    setGroupOverrides(current => ({ ...current, [item.to]: !isGroupOpen(item) }));
  };

  const renderLink = (item: AdminNavItem, nested = false) => (
    <NavLink
      to={item.to}
      end={item.end}
      // Doubles as the tooltip for the collapsed rail, where the label is
      // visually hidden.
      title={t(item.labelKey)}
      className={({ isActive }) =>
        [
          'admin-shell__nav-link',
          nested ? 'admin-shell__nav-link--nested' : '',
          isActive ? 'admin-shell__nav-link--active' : '',
        ]
          .filter(Boolean)
          .join(' ')
      }>
      <span className={`ui-icon-chip ui-icon-chip--${item.tone} admin-shell__nav-icon`}>
        <Icon name={item.icon} size={nested ? 'sm' : 'md'} />
      </span>
      <span className="admin-shell__nav-label">{t(item.labelKey)}</span>
      {item.sample ? (
        <span className="admin-shell__nav-badge">{t('sample.badge')}</span>
      ) : null}
    </NavLink>
  );

  return (
    <div className={`admin-shell${railCollapsed ? ' admin-shell--collapsed' : ''}`}>
      <a className="admin-shell__skip-link" href="#admin-content">
        {t('nav.skipToContent')}
      </a>

      {/* Tapping outside the overlay rail is the gesture people try first. */}
      {mobileNavOpen ? (
        <button
          type="button"
          className="admin-shell__backdrop"
          aria-label={t('nav.closeMenu')}
          onClick={() => setMobileNavOpen(false)}
        />
      ) : null}

      <aside
        id="admin-sidebar"
        className={`admin-shell__sidebar${
          mobileNavOpen ? ' admin-shell__sidebar--open' : ''
        }`}>
        <div className="admin-shell__brand">
          <Link className="admin-shell__brand-link" to="/">
            <BrandLogo compact={railCollapsed} />
          </Link>
          <span className="admin-shell__brand-sub">{t('app.subtitle')}</span>
        </div>

        <nav className="admin-shell__nav" aria-label={t('nav.primaryLabel')}>
          {NAV_SECTIONS.map(section => (
            <div className="admin-shell__nav-group" key={section.titleKey}>
              <h2 className="admin-shell__nav-title">{t(section.titleKey)}</h2>
              <ul>
                {section.items.map(item => {
                  const groupId = `nav-group-${item.to.replace(/\W+/g, '-')}`;
                  const open = isGroupOpen(item);
                  return (
                    <li key={item.to}>
                      {item.children ? (
                        <div className="admin-shell__nav-row">
                          {renderLink(item)}
                          <button
                            type="button"
                            className="admin-shell__nav-disclosure"
                            aria-expanded={open}
                            aria-controls={groupId}
                            aria-label={t(
                              open ? 'nav.collapseGroup' : 'nav.expandGroup',
                              { name: t(item.labelKey) },
                            )}
                            onClick={() => toggleGroup(item)}>
                            <Icon name="chevronDown" size="sm" />
                          </button>
                        </div>
                      ) : (
                        renderLink(item)
                      )}
                      {item.children ? (
                        <ul
                          id={groupId}
                          className="admin-shell__nav-children"
                          hidden={!open}>
                          {item.children.map(child => (
                            <li key={child.to}>{renderLink(child, true)}</li>
                          ))}
                        </ul>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="admin-shell__rail-footer">
          <button
            type="button"
            className="admin-shell__rail-toggle"
            // The label already names the next action, so aria-pressed would
            // invert it ("Expand menu, pressed"). aria-expanded is the state.
            aria-expanded={!railCollapsed}
            aria-controls="admin-sidebar"
            onClick={() => setRailCollapsed(collapsed => !collapsed)}>
            <Icon name={railCollapsed ? 'chevronRight' : 'chevronLeft'} size="sm" />
            {/* Visually hidden once collapsed, so it still has to name the next
                action rather than the current width. */}
            <span className="admin-shell__nav-label">
              {t(railCollapsed ? 'nav.expandRail' : 'nav.collapseRail')}
            </span>
          </button>
        </div>
      </aside>

      <div className="admin-shell__main">
        <header className="admin-shell__topbar">
          <div className="admin-shell__topbar-inner">
            <IconButton
              icon="menu"
              variant="plain"
              className="admin-shell__menu-toggle"
              label={mobileNavOpen ? t('nav.closeMenu') : t('nav.openMenu')}
              aria-expanded={mobileNavOpen}
              aria-controls="admin-sidebar"
              onClick={() => setMobileNavOpen(open => !open)}
            />
            <IconButton
              icon="sidebar"
              variant="plain"
              className="admin-shell__topbar-rail-toggle"
              label={railCollapsed ? t('nav.expandRail') : t('nav.collapseRail')}
              aria-expanded={!railCollapsed}
              aria-controls="admin-sidebar"
              onClick={() => setRailCollapsed(collapsed => !collapsed)}
            />

            <nav
              className="admin-shell__breadcrumb"
              aria-label={t('nav.breadcrumbLabel')}>
              <ol>
                <li>
                  <Link to="/">{t('nav.home')}</Link>
                </li>
                {/* A sub-module gets both hops, so "Reported content / Pending
                    review" reads as a place rather than a leaf. */}
                {parentItem && parentItem.to !== '/' ? (
                  <li>
                    {parentItem === activeItem ? (
                      <span aria-current="page">{t(parentItem.labelKey)}</span>
                    ) : (
                      <Link to={parentItem.to}>{t(parentItem.labelKey)}</Link>
                    )}
                  </li>
                ) : null}
                {activeItem && activeItem !== parentItem && activeItem.to !== '/' ? (
                  <li aria-current="page">{t(activeItem.labelKey)}</li>
                ) : null}
              </ol>
            </nav>

            <div className="admin-shell__session">
              {displayName ? (
                <span className="admin-shell__session-name">
                  {t('nav.signedInAs', { name: displayName })}
                </span>
              ) : null}
              {MEMBER_SITE_URL ? (
                <a
                  className="admin-shell__site-link"
                  href={MEMBER_SITE_URL}
                  target="_blank"
                  rel="noreferrer">
                  <Icon name="externalLink" size="sm" />
                  <span className="admin-shell__site-link-label">
                    {t('nav.memberSite')}
                  </span>
                </a>
              ) : null}
              <IconButton
                icon={isDark ? 'sun' : 'moon'}
                variant="plain"
                tone={isDark ? 'amber' : 'indigo'}
                label={isDark ? t('nav.themeLight') : t('nav.themeDark')}
                onClick={toggleTheme}
              />
              <Button variant="secondary" small onClick={signOut}>
                <Icon name="logOut" size="sm" />
                {t('nav.signOut')}
              </Button>
            </div>
          </div>
        </header>
        <div className="admin-shell__scroll" ref={scrollRef}>
          <main id="admin-content" className="admin-shell__content" tabIndex={-1}>
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
